import { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { CaretLeft, CaretRight, Tag } from "@phosphor-icons/react";
import { Reveal } from "./motion";
import { useI18n } from "@/i18n";

const spreadData = [
  {
    n: "01",
    img: "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    brands: ["Nestlé", "Coca-Cola", "Bimbo", "Colgate", "Procter & Gamble", "Unilever", "Kellogg's", "Sabritas", "Gerber", "Nescafé", "La Costeña", "Herdez"],
  },
  {
    n: "02",
    img: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    brands: ["Samsung", "Xiaomi", "Anker", "JBL", "Logitech", "HP", "Sony", "TP-Link", "Kingston", "Belkin", "Epson", "Canon"],
  },
  {
    n: "03",
    img: "https://images.pexels.com/photos/4481328/pexels-photo-4481328.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    brands: ["Truper", "Black+Decker", "3M", "Stanley", "Rubbermaid", "Pyrex", "Oster", "Tramontina", "Mabe", "Hamilton Beach", "Coby", "Vasconia"],
  },
  {
    n: "04",
    img: "https://images.unsplash.com/photo-1450778869180-41d0601e046e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    brands: ["Pedigree", "Whiskas", "Purina", "Royal Canin", "Pro Plan", "Dog Chow", "Cat Chow", "Hill's", "Nupec", "Ganacan", "Friskies", "Felix"],
  },
];

export function Brands() {
  const { t } = useI18n();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 30 });
  const [selected, setSelected] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  return (
    <section id="marcas" className="bg-[#050505] py-24 md:py-32 border-t border-white/10">
      <div className="w-full max-w-screen-2xl mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-12">
          <Reveal className="md:col-span-8">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#ff4500] mb-4">{t.brands.kicker}</p>
            <h2 className="font-clash font-black uppercase leading-[0.95] text-[#f5f5f5] text-4xl md:text-6xl tracking-tight">
              {t.brands.headingA} <br /> {t.brands.headingB}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-4">
            <p className="font-plex text-[#a3a3a3] md:text-right">{t.brands.sub}</p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="relative border border-white/10 bg-[#0a0a0a]">
            <div className="overflow-hidden" ref={emblaRef} data-testid="brands-carousel">
              <div className="flex">
                {spreadData.map((s, idx) => (
                  <div key={s.n} className="flex-[0_0_100%] min-w-0">
                    <div className="grid md:grid-cols-2 min-h-[520px]">
                      <div className="relative overflow-hidden border-b md:border-b-0 md:border-r border-white/10 p-8 md:p-12 flex flex-col justify-between">
                        <img src={s.img} alt={t.brands.categoryNames[idx]} className="absolute inset-0 w-full h-full object-cover opacity-25" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
                        <div className="relative z-10 flex items-center justify-between">
                          <span className="font-mono text-xs uppercase tracking-widest text-[#a3a3a3]">{t.brands.lineWord} / {s.n}</span>
                          <span className="font-mono text-[10px] uppercase tracking-wider border border-white/20 text-[#f5f5f5] px-2 py-1">{t.brands.subtitles[idx]}</span>
                        </div>
                        <div className="relative z-10">
                          <span className="font-clash font-black text-[7rem] md:text-[10rem] leading-none text-white/10 block">{s.n}</span>
                          <h3 className="font-clash font-black uppercase text-4xl md:text-5xl text-[#f5f5f5] tracking-tight -mt-6">{t.brands.categoryNames[idx]}</h3>
                        </div>
                      </div>

                      <div className="p-8 md:p-12">
                        <p className="font-mono text-[11px] uppercase tracking-widest text-[#555] mb-6">{t.brands.available}</p>
                        <motion.div
                          key={s.n}
                          initial="hidden"
                          animate={selected === idx ? "show" : "hidden"}
                          variants={{ show: { transition: { staggerChildren: 0.04 } } }}
                          className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/10 border border-white/10"
                        >
                          {s.brands.map((b) => (
                            <motion.div
                              key={b}
                              variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}
                              className="group bg-[#0a0a0a] hover:bg-[#111] transition-colors aspect-[16/9] flex items-center justify-center px-3 text-center"
                            >
                              <span className="font-clash font-bold text-sm md:text-base text-[#a3a3a3] group-hover:text-[#ff4500] transition-colors leading-tight">
                                {b}
                              </span>
                            </motion.div>
                          ))}
                        </motion.div>
                        <div className="mt-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-[#555]">
                          <Tag size={14} className="text-[#ff4500]" /> {s.brands.length} {t.brands.countSuffix}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden md:block absolute top-0 bottom-0 left-1/2 w-px bg-white/10 pointer-events-none" />

            <button
              onClick={scrollPrev}
              data-testid="brands-prev"
              aria-label="previous"
              className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center bg-black/70 border border-white/15 text-[#f5f5f5] hover:bg-[#ff4500] hover:text-[#050505] transition-colors"
            >
              <CaretLeft size={20} weight="bold" />
            </button>
            <button
              onClick={scrollNext}
              data-testid="brands-next"
              aria-label="next"
              className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center bg-black/70 border border-white/15 text-[#f5f5f5] hover:bg-[#ff4500] hover:text-[#050505] transition-colors"
            >
              <CaretRight size={20} weight="bold" />
            </button>
          </div>
        </Reveal>

        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {spreadData.map((s, i) => (
              <button
                key={s.n}
                onClick={() => scrollTo(i)}
                data-testid={`brands-dot-${i}`}
                aria-label={`${t.brands.pageWord} ${i + 1}`}
                className={`h-1.5 transition-all duration-300 ${selected === i ? "w-10 bg-[#ff4500]" : "w-4 bg-white/20 hover:bg-white/40"}`}
              />
            ))}
          </div>
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#555]" data-testid="brands-page-indicator">
            {t.brands.pageWord} {String(selected + 1).padStart(2, "0")} / {String(spreadData.length).padStart(2, "0")}
          </span>
        </div>
      </div>
    </section>
  );
}
